/* The chart frame.
 *
 * Axis labels are HTML, the plot is SVG. That split is what lets the plot
 * use preserveAspectRatio="none" and stretch to any width while the labels
 * stay upright and legibly sized — an all-SVG chart would shear its own
 * text. The SVG is always a 1000 × height box, so every chart in the app
 * does its geometry in the same coordinates.
 */

import type { ReactNode } from 'react'
import { HoverGuide, Tooltip, useBandHover } from './hover'

export const PLOT_W = 1000

export function Plot({
  height, yLabels, yLabelsRight, yTitle, yTitleRight, xLabels, xTitle, overlay,
  bands = 0, tooltip, children,
}: {
  height: number
  yLabels: string[]
  yLabelsRight?: string[]
  yTitle?: string
  yTitleRight?: string
  xLabels?: ReactNode
  xTitle?: string
  /** HTML drawn on top of the plot, in the plot's own box. Data labels live
   *  here rather than in the SVG: the SVG is stretched horizontally, which
   *  would shear any text inside it. */
  overlay?: ReactNode
  /** Number of equal bands the plot is divided into — pass alongside
   *  `tooltip` to turn on the hover readout. */
  bands?: number
  /** Contents of the hover readout for band `i`. Return null to show none. */
  tooltip?: (i: number) => ReactNode
  children: ReactNode
}) {
  const hover = useBandHover(bands)
  const live = tooltip && bands > 0
  const tip = live && hover.point ? tooltip(hover.point.i) : null

  const cols = [
    yTitle ? '16px' : null,
    '74px',        // wide enough for a full rupee figure without wrapping
    '1fr',
    yLabelsRight ? '46px' : null,
    yTitleRight ? '16px' : null,
  ].filter(Boolean).join(' ')

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6 }}>
        {yTitle ? <div className="axis-title" style={vertical}>{yTitle}</div> : null}

        {/* column-reverse so the array reads low-to-high but paints bottom-up */}
        <div style={axisCol}>
          {yLabels.map((t, i) => <span key={i}>{t}</span>)}
        </div>

        <div
          ref={hover.ref}
          style={{ position: 'relative', height }}
          {...(live ? hover.bind : {})}
        >
          <svg
            viewBox={`0 0 ${PLOT_W} ${height}`}
            preserveAspectRatio="none"
            style={{
              width: '100%', height, display: 'block',
              borderLeft: '1px solid var(--color-neutral-500)',
              borderBottom: '1px solid var(--color-neutral-500)',
            }}
          >
            {children}
            {/* Drawn last so the guide sits over the series, and centred on
                the band rather than the cursor so it lines up with the bar. */}
            {live && hover.point ? (
              <HoverGuide
                x={((hover.point.i + 0.5) / bands) * PLOT_W}
                height={height}
              />
            ) : null}
          </svg>
          {overlay ? (
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
            }}>{overlay}</div>
          ) : null}
          {tip && hover.point ? (
            <Tooltip
              x={hover.ref.current
                ? ((hover.point.i + 0.5) / bands) * hover.ref.current.clientWidth
                : hover.point.x}
              y={hover.point.y}
              width={hover.ref.current?.clientWidth ?? 0}
            >{tip}</Tooltip>
          ) : null}
        </div>

        {yLabelsRight ? (
          <div style={{ ...axisCol, alignItems: 'flex-start' }}>
            {yLabelsRight.map((t, i) => <span key={i}>{t}</span>)}
          </div>
        ) : null}
        {yTitleRight ? (
          <div className="axis-title" style={{ writingMode: 'vertical-rl', textAlign: 'center' }}>
            {yTitleRight}
          </div>
        ) : null}
      </div>

      {xLabels ? (
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 6, marginTop: 5 }}>
          {yTitle ? <div /> : null}
          <div />
          <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr' }}>
            {xLabels}
          </div>
          {yLabelsRight ? <div /> : null}
          {yTitleRight ? <div /> : null}
        </div>
      ) : null}

      {xTitle ? (
        <div className="axis-title" style={{ textAlign: 'center', marginTop: 8 }}>{xTitle}</div>
      ) : null}
    </>
  )
}

const vertical: React.CSSProperties = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  textAlign: 'center',
}

const axisCol: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  height: '100%',
  margin: '-8px 0',
  fontSize: 11,
  color: 'var(--color-neutral-600)',
  fontVariantNumeric: 'tabular-nums',
  // A wrapped tick reads as two labels and shifts the whole scale visually,
  // so labels never wrap even if that means they overhang the column.
  whiteSpace: 'nowrap',
}

/** Horizontal grid lines at the given y positions. */
export function GridLines({ ys }: { ys: number[] }) {
  return (
    <>
      {ys.map((y, i) => (
        <line key={i} x1="0" y1={y} x2={PLOT_W} y2={y}
          stroke="var(--c-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ))}
    </>
  )
}
